import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { endpoints } from "@/lib/api";
import { fmtMoney } from "@/lib/utils-format";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["hsl(var(--primary))", "hsl(var(--secondary))", "hsl(var(--accent))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => { endpoints.reports().then(r => setData(r.data)); }, []);

  if (!data) return <Layout title="Reports"><div className="text-muted-foreground">Loading...</div></Layout>;

  const eq = data.equipment_expenses || {};
  const stats = [
    { label: "Total Revenue", value: fmtMoney(data.total_revenue), tone: "text-secondary" },
    { label: "Total Expenses", value: fmtMoney(data.expenses), tone: "text-destructive" },
    { label: "Profit", value: fmtMoney(data.profit), tone: data.profit >= 0 ? "text-secondary" : "text-destructive" },
    { label: "Jobs Completed", value: data.jobs_completed },
    { label: "Outstanding", value: fmtMoney(data.outstanding), tone: "text-amber-600 dark:text-amber-400" },
    { label: "Payroll Paid", value: fmtMoney(data.payroll) },
  ];

  return (
    <Layout title="Reports" subtitle="Live financials from real data — updates automatically">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="reports-stats">
        {stats.map(s => (
          <div key={s.label} className="stat-card">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{s.label}</div>
            <div className={`font-display text-2xl font-bold mt-2 ${s.tone || ""}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6 rounded-xl">
          <div className="font-display text-lg font-semibold mb-4">Equipment Expenses</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Row label="Purchase" value={fmtMoney(eq.purchase || 0)} />
            <Row label="Rental" value={fmtMoney(eq.rental || 0)} />
            <Row label="Maintenance" value={fmtMoney(eq.maintenance || 0)} />
            <Row label="Fuel" value={fmtMoney(eq.fuel || 0)} />
            <Row label="Repair" value={fmtMoney(eq.repair || 0)} />
            <div className="col-span-2 mt-2 p-3 rounded-lg bg-muted/40 flex items-center justify-between">
              <span className="font-semibold">Equipment Total</span>
              <span className="font-mono font-bold">{fmtMoney(eq.total || 0)}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-xl">
          <div className="font-display text-lg font-semibold mb-4">Expenses by Category</div>
          {data.expense_breakdown.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No expenses recorded yet. Add expenses from the Expenses page to populate this.</div>
          ) : (
            <div className="space-y-2 text-sm">
              {data.expense_breakdown.map((e) => (
                <Row key={e.category} label={e.category} value={fmtMoney(e.amount)} />
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-xl">
          <div className="font-display text-lg font-semibold mb-4">Top Customers</div>
          <div className="h-64" data-testid="top-customers-chart" style={{ minHeight: 220 }}>
            {data.top_customers.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">No customer revenue yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_customers} layout="vertical" margin={{ top: 5, right: 10, left: 50, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false}/>
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v)=>`$${v/1000}k`}/>
                  <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={11} width={100}/>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem", fontSize: "12px" }} formatter={(v)=>fmtMoney(v)}/>
                  <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-6 rounded-xl">
          <div className="font-display text-lg font-semibold mb-4">Revenue by Service</div>
          <div className="h-64" data-testid="by-service-chart" style={{ minHeight: 220 }}>
            {data.by_service.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">Complete some jobs to populate this chart.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.by_service} dataKey="revenue" nameKey="service" cx="50%" cy="50%" outerRadius={90} label={(d) => d.service}>
                    {data.by_service.map((entry, i) => <Cell key={entry.service} fill={COLORS[i % COLORS.length]}/>)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem", fontSize: "12px" }} formatter={(v)=>fmtMoney(v)}/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between p-2 rounded-md border border-border bg-card">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </div>
  );
}
